import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ethers } from 'ethers';
import { toast } from 'sonner';
import { Wallet, Loader2, ArrowDown, ExternalLink, ShieldCheck } from 'lucide-react';
import { OfficialToken } from './OfficialToken';

const B = process.env.REACT_APP_BACKEND_URL;
const BASE_CHAIN = '0x2105';
const ROUTER_ABI = ['function exactInput((bytes path,address recipient,uint256 amountIn,uint256 amountOutMinimum)) payable returns (uint256)'];
const ERC20_ABI = ['function allowance(address,address) view returns (uint256)', 'function approve(address,uint256) returns (bool)', 'function balanceOf(address) view returns (uint256)'];
const SLIPPAGE = 0.01;

const ensureBase = async () => {
  try { await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BASE_CHAIN }] }); }
  catch (e) {
    if (e.code !== 4902) throw e;
    await window.ethereum.request({ method: 'wallet_addEthereumChain', params: [{ chainId: BASE_CHAIN, chainName: 'Base', nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, rpcUrls: ['https://mainnet.base.org'], blockExplorerUrls: ['https://basescan.org'] }] });
  }
};

const useQuote = (pay, amount) => {
  const [q, setQ] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setQ(null); setErr('');
    if (!pay || !(Number(amount) > 0)) return undefined;
    const t = setTimeout(() => axios.get(`${B}/api/public/buy-quote`, { params: { pay, amount } })
      .then(r => setQ(r.data)).catch(e => setErr(e.response?.data?.detail?.toString() || 'No quote right now')), 450);
    return () => clearTimeout(t);
  }, [pay, amount]);
  return { q, err };
};

export const BuyBethWidget = () => {
  const [pay, setPay] = useState('USDC');
  const [amount, setAmount] = useState('100');
  const [addr, setAddr] = useState(null);
  const [step, setStep] = useState('');
  const [tx, setTx] = useState(null);
  const cardAmt = Math.min(5000, Math.max(5, Number(amount) || 5));
  const cardOk = Number(amount) >= 5 && Number(amount) <= 5000;
  const cardHref = `${localStorage.getItem('pas_client_token') ? '/pas/dashboard?tab=buy-card' : '/pas/signup?next=buy-card'}&amount=${cardAmt}`;
  const { q, err } = useQuote(pay === 'CARD' ? null : pay, amount);

  const connect = async () => {
    if (!window.ethereum) { toast.error('No wallet found. Install MetaMask or open this page in your wallet app.'); return null; }
    const [a] = await window.ethereum.request({ method: 'eth_requestAccounts' });
    await ensureBase();
    setAddr(a);
    return a;
  };

  const buy = async () => {
    setTx(null);
    try {
      const a = addr || await connect();
      if (!a || !q) return;
      await ensureBase();
      const signer = new ethers.providers.Web3Provider(window.ethereum).getSigner();
      const minOut = ethers.BigNumber.from(q.raw_out).mul(Math.round((1 - SLIPPAGE) * 1000)).div(1000);
      const value = pay === 'ETH' ? ethers.BigNumber.from(q.raw_in) : 0;
      if (pay === 'USDC') {
        const usdc = new ethers.Contract(q.usdc, ERC20_ABI, signer);
        if ((await usdc.balanceOf(a)).lt(q.raw_in)) { toast.error('Not enough USDC on Base in this wallet'); return; }
        if ((await usdc.allowance(a, q.router)).lt(q.raw_in)) {
          setStep('Approve USDC in your wallet…');
          await (await usdc.approve(q.router, q.raw_in)).wait();
        }
      }
      setStep('Confirm the purchase in your wallet…');
      const router = new ethers.Contract(q.router, ROUTER_ABI, signer);
      const t = await router.exactInput({ path: q.path, recipient: a, amountIn: q.raw_in, amountOutMinimum: minOut }, { value });
      setStep('Waiting for Base to confirm…');
      await t.wait();
      setTx(t.hash);
      toast.success(`Bought about ${Number(q.beth_out).toLocaleString(undefined, { maximumFractionDigits: 2 })} BETH USD`);
      window.ethereum.request({ method: 'wallet_watchAsset', params: { type: 'ERC20', options: { address: q.beth, symbol: 'BETH', decimals: 18, image: `${B}/api/token-list/beth-usd.png` } } }).catch(() => {});
    } catch (e) {
      toast.error(e.code === 4001 || e.code === 'ACTION_REJECTED' ? 'Cancelled in your wallet' : (e.reason || e.message || 'Purchase failed').slice(0, 140));
    } finally { setStep(''); }
  };

  return (
    <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl p-6 space-y-4 shadow-2xl shadow-emerald-500/10" data-testid="buy-beth-widget">
      <div className="flex items-center gap-3">
        <img src={`${B}/api/token-list/beth-usd.png`} alt="BETH USD" className="w-9 h-9 rounded-full" />
        <div><p className="text-white font-bold">Buy BETH USD</p><p className="text-[11px] text-slate-400">On Base · swaps via Uniswap in your own wallet</p></div>
      </div>
      <div className="bg-slate-950 border border-slate-700 rounded-xl p-3">
        <p className="text-[10px] uppercase text-slate-500">You pay</p>
        <div className="flex items-center gap-2 mt-1">
          <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="flex-1 min-w-0 bg-transparent text-2xl text-white font-mono outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-testid="buy-widget-amount" />
          <div className="flex shrink-0 bg-slate-800 rounded-full p-0.5">
            {['USDC', 'ETH', 'CARD'].map(t => <button key={t} onClick={() => setPay(t)} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${pay === t ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`} data-testid={`buy-widget-pay-${t.toLowerCase()}`}>{t === 'CARD' ? 'Card' : t}</button>)}
          </div>
        </div>
      </div>
      <ArrowDown className="w-4 h-4 text-slate-500 mx-auto" />
      <div className="bg-slate-950 border border-slate-700 rounded-xl p-3">
        <p className="text-[10px] uppercase text-slate-500">You receive (about)</p>
        <p className="text-2xl text-emerald-300 font-mono mt-1" data-testid="buy-widget-receive">{pay === 'CARD' ? Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }) : q ? Number(q.beth_out).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'} <span className="text-sm text-slate-400">BETH</span></p>
        {err && <p className="text-[11px] text-red-400" data-testid="buy-widget-error">{err}</p>}
      </div>
      {pay === 'CARD' ? (cardOk ? (
        <a href={cardHref} className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="buy-widget-card-link">
          Pay by card · ${(Number(amount) * 1.03).toFixed(2)} incl. 3% fee
        </a>
      ) : <p className="w-full py-3 rounded-xl bg-slate-800 text-amber-300 text-sm text-center" data-testid="buy-widget-card-range">Card purchases are $5 – $5,000</p>) : (
      <button onClick={buy} disabled={!!step || !q} className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="buy-widget-submit">
        {step ? <><Loader2 className="w-4 h-4 animate-spin" /> {step}</> : addr ? 'Buy BETH USD' : <><Wallet className="w-4 h-4" /> Connect wallet & buy</>}
      </button>
      )}
      {pay === 'CARD' && <p className="text-[11px] text-slate-400 text-center" data-testid="buy-widget-card-note">No crypto wallet needed: you get a free BETH account, and the BETH USD goes straight into it. $5–$5,000 per purchase.</p>}
      {addr && <p className="text-[11px] text-slate-400 text-center" data-testid="buy-widget-address">Wallet {addr.slice(0, 6)}…{addr.slice(-4)} on Base</p>}
      {tx && <a href={`https://basescan.org/tx/${tx}`} target="_blank" rel="noreferrer" className="block text-center text-xs text-cyan-300 underline" data-testid="buy-widget-tx">View purchase on BaseScan <ExternalLink className="w-3 h-3 inline" /></a>}
      <OfficialToken compact />
      <p className="text-[10px] text-slate-500 flex items-start gap-1"><ShieldCheck className="w-3 h-3 mt-0.5 text-emerald-500 shrink-0" /> <span>1% max slippage. You need a little ETH on Base for gas. <a href="/reserves" className="underline">See the reserves</a>. Not FDIC insured.</span></p>
    </div>
  );
};
